import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ScrollText, Users, UserCog, Boxes, Plus, ArrowRight, TrendingUp, Calendar,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, accent, href, testid }) {
  const inner = (
    <Card className="p-4 sm:p-6 border-border h-full hover:-translate-y-0.5 hover:shadow-sm hover:border-zinc-900 transition-all cursor-pointer">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="overline text-zinc-500">{label}</div>
          <div className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1 num truncate">{value ?? 0}</div>
        </div>
        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md grid place-items-center ${accent} flex-shrink-0`}>
          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={1.7} />
        </div>
      </div>
    </Card>
  );
  return href ? (
    <Link to={href} data-testid={testid}>
      {inner}
    </Link>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}

function fmtInr(n) {
  return `₹ ${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function VoucherDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/voucher/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const k = data.kpis;
  const maxAmt = Math.max(1, ...data.graph_30d.map((d) => d.amount || 0));

  return (
    <div className="space-y-8" data-testid="voucher-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Voucher Dashboard</h1>
          <p className="text-zinc-500 mt-2 text-sm">Travelling Vouchers, Delivery Challans, Masters & KYC.</p>
        </div>
        <Link to="/voucher/vouchers/new">
          <Button className="bg-zinc-900 hover:bg-zinc-800 h-10" data-testid="voucher-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Voucher
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi label="Vouchers" value={k.total_vouchers} icon={ScrollText} accent="bg-zinc-900 text-white" href="/voucher/vouchers" testid="kpi-total-vouchers" />
        <Kpi label="Today" value={k.today_vouchers} icon={Calendar} accent="bg-amber-100 text-amber-800" testid="kpi-today-vouchers" />
        <Kpi label="This Month" value={k.month_vouchers} icon={TrendingUp} accent="bg-emerald-100 text-emerald-800" testid="kpi-month-vouchers" />
        <Kpi label="Customers" value={k.total_customers} icon={Users} accent="bg-blue-100 text-blue-800" href="/voucher/customers" testid="kpi-customers" />
        <Kpi label="Items" value={k.total_items} icon={Boxes} accent="bg-violet-100 text-violet-800" href="/voucher/items" testid="kpi-items" />
        <Kpi label="Salesmen" value={k.total_salesmen} icon={UserCog} accent="bg-rose-100 text-rose-800" href="/voucher/salesmen" testid="kpi-salesmen" />
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl tracking-tight">Voucher Value — Last 30 days</h3>
        </div>
        <div className="flex items-end gap-1 h-40 sm:h-48 md:h-52" data-testid="voucher-30d-graph">
          {data.graph_30d.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center group" title={`${d.date}: ${fmtInr(d.amount)}`}>
              <div
                className="w-full bg-zinc-900 group-hover:bg-gold-500 transition-colors rounded-sm min-h-[2px]"
                style={{ height: `${((d.amount || 0) / maxAmt) * 100}%` }}
              />
              <div className="text-[9px] text-zinc-400 mt-1 hidden sm:block">{d.date.slice(5)}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-zinc-400 mt-2 sm:hidden">
          <span>{data.graph_30d[0]?.date}</span>
          <span>{data.graph_30d[data.graph_30d.length - 1]?.date}</span>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-xl tracking-tight">Recent Vouchers</h3>
            <Link to="/voucher/vouchers" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.recent_vouchers.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No vouchers yet.</div>
          ) : (
            <div className="divide-y divide-border" data-testid="recent-vouchers-list">
              {data.recent_vouchers.map((v) => (
                <Link
                  key={v.id}
                  to={`/voucher/vouchers/${v.id}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded-md gap-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-500">{v.voucher_no}</span>
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${v.voucher_type === "travelling" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                        {v.voucher_type === "travelling" ? "Travelling" : "Delivery"}
                      </span>
                    </div>
                    <div className="font-medium text-sm mt-1 truncate">{v.to_customer_name || "—"}</div>
                  </div>
                  <div className="text-right sm:text-left w-full sm:w-auto">
                    <div className="text-sm font-semibold num">{fmtInr(v.grand_total)}</div>
                    <div className="text-xs text-zinc-500">{v.voucher_date?.slice(0, 10)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-xl tracking-tight">Recent Customers</h3>
            <Link to="/voucher/customers" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {data.recent_customers.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No customers yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {data.recent_customers.map((c) => (
                <li key={c.id} className="py-3">
                  <div className="font-medium text-sm">{c.customer_name}</div>
                  <div className="text-xs text-zinc-500 truncate flex flex-col sm:flex-row sm:items-center gap-1">
                    <span>{c.company_name || "—"}</span>
                    <span className="hidden sm:inline">·</span>
                    <span>{c.mobile || "no mobile"}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
